import React, { useState } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    InputAdornment,
    Stack,
    Typography,
    Checkbox,
    CircularProgress,
    Alert,
    Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FilterDropdown from '../FilterDropdown';
import CommonCreateButton from '../CommonCreateButton';
import { CommonAccountTableProps } from '../../../types';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

const CommonAccountTable = <T,>({
    // Required
    columns,
    data,
    loading = false,

    // Search and Filter props
    searchPlaceholder = 'Search...',
    onSearch,
    showSearchBar = true,
    searchValue: externalSearchValue,
    showDateRange = true,
    dateRangeOptions = [
        { value: 'all', label: 'All Dates' },
        { value: 'today', label: 'Today' },
        { value: 'yesterday', label: 'Yesterday' },
        { value: 'this_week', label: 'This Week' },
        { value: 'this_month', label: 'This Month' },
        { value: 'last_month', label: 'Last Month' },
        { value: 'custom', label: 'Custom Range' }
    ],
    selectedDateRange: externalDateRange,
    onDateRangeChange,
    showFilter = true,
    filterOptions = [
        { value: 'all', label: 'All' },
        { value: 'GST', label: 'GST' },
        { value: 'NON-GST', label: 'Non-GST' }
    ],
    selectedFilter: externalFilter,
    onFilterChange,
    filterLabel = 'Type',
    filterMultiple = false,

    // Custom date range props
    startDate,
    endDate,
    onCustomDateClick,

    // Other props
    title,
    selectable = false,
    selectedRows: externalSelectedRows,
    onRowSelect,
    showCreateButton = true,
    createButtonProps = {},
    customActions,
    error,
    emptyMessage = 'No data found',
    sx = {},
    tableHeight = 600,

    // Row click navigation
    clickableRows = true,
    onRowClick,
    navigatePath,
    idField = 'id',
    getRowLink,

}: CommonAccountTableProps<T>) => {
    // Internal state
    const [internalSearch, setInternalSearch] = useState('');
    const [internalDateRange, setInternalDateRange] = useState('all');
    const [internalFilter, setInternalFilter] = useState<string | string[]>(filterMultiple ? [] : 'all');
    const [internalSelectedRows, setInternalSelectedRows] = useState<any[]>([]);

    const navigate = useNavigate();

    // Use controlled or uncontrolled state
    const searchValue = externalSearchValue !== undefined ? externalSearchValue : internalSearch;
    const selectedDateRange = externalDateRange !== undefined ? externalDateRange : internalDateRange;
    const selectedFilter = externalFilter !== undefined ? externalFilter : internalFilter;
    const selectedRows = externalSelectedRows !== undefined ? externalSelectedRows : internalSelectedRows;

    // Format date function
    const formatDate = (date: Date | null) => {
        if (!date) return '';
        return format(date, 'dd/MM/yyyy');
    };

    // Handle row click
    const handleRowClick = (row: any, event: React.MouseEvent) => {
        if ((event.target as HTMLElement).closest('input[type="checkbox"]') ||
            (event.target as HTMLElement).closest('button')) {
            return;
        }

        if (onRowClick) {
            onRowClick(row);
            return;
        }

        if (clickableRows) {
            if (getRowLink) {
                const link = getRowLink(row);
                if (link) {
                    navigate(link);
                }
            } else if (navigatePath) {
                const id = row[idField];
                if (id) {
                    navigate(`${navigatePath}/${id}`);
                }
            }
        }
    };

    // Handle checkbox selection
    const handleRowSelect = (rowId: string, event: React.MouseEvent) => {
        event.stopPropagation();

        const selectedIndex = selectedRows.indexOf(rowId);
        let newSelected: any[] = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selectedRows, rowId);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selectedRows.slice(1));
        } else if (selectedIndex === selectedRows.length - 1) {
            newSelected = newSelected.concat(selectedRows.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selectedRows.slice(0, selectedIndex),
                selectedRows.slice(selectedIndex + 1)
            );
        }

        setInternalSelectedRows(newSelected);
        if (onRowSelect) {
            onRowSelect(newSelected);
        }
    };

    // Handle select all click
    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelected = data?.map((row) => row[idField]) ?? [];
            setInternalSelectedRows(newSelected);
            if (onRowSelect) {
                onRowSelect(newSelected);
            }
            return;
        }
        setInternalSelectedRows([]);
        if (onRowSelect) {
            onRowSelect([]);
        }
    };

    const isSelected = (rowId: string) => selectedRows.indexOf(rowId) !== -1;

    // Handle search
    const handleSearch = (value: string) => {
        if (onSearch) {
            onSearch(value);
        } else {
            setInternalSearch(value);
        }
    };

    // Handle date range change
    const handleDateRangeChange = (range: string | string[]) => {
        const value = Array.isArray(range) ? (range[0] ?? 'all') : (range || 'all');

        if (value === 'custom' && onCustomDateClick) {
            onCustomDateClick();
            return;
        }

        if (onDateRangeChange) {
            onDateRangeChange(value);
        } else {
            setInternalDateRange(value);
        }
    };

    // Handle filter change
    const handleFilterChange = (filter: string | string[]) => {
        if (onFilterChange) {
            onFilterChange(filter);
        } else {
            setInternalFilter(filter);
        }
    };

    // Handle custom date range click
    const handleCustomDateClick = () => {
        if (onCustomDateClick) {
            onCustomDateClick();
        } else if (onDateRangeChange) {
            onDateRangeChange('custom');
        }
    };

    return (
        <Box sx={{ width: '100%', ...sx }}>
            {/* Header with filters */}
            <Box sx={{ mb: 3 }}>
                {title && (
                    <Typography variant="h6" pb={0}>
                        {title}
                    </Typography>
                )}

                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    justifyContent="space-between"
                    sx={{ mb: 2 }}
                >
                    {/* Left side: Search and filters */}
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        flex={1}
                    >
                        {/* Search bar */}
                        {showSearchBar && (
                            <TextField
                                placeholder={searchPlaceholder}
                                value={searchValue}
                                onChange={(e) => handleSearch(e.target.value)}
                                size="small"
                                sx={{ minWidth: 250 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}

                        {/* Custom Date Range Display when custom is selected */}
                        {showDateRange && selectedDateRange === 'custom' && startDate && endDate ? (
                            <Button
                                variant="outlined"
                                size="medium"
                                color='primary'
                                startIcon={<CalendarTodayIcon />}
                                onClick={handleCustomDateClick}
                                sx={{
                                    minWidth: 200,
                                    justifyContent: 'flex-start',
                                    textTransform: 'none',
                                    fontWeight: 'normal'
                                }}
                            >
                                {`${formatDate(startDate)} - ${formatDate(endDate)}`}
                            </Button>
                        ) : (
                            /* Regular Date Range Dropdown (only show when not custom or no dates) */
                            showDateRange && (
                                <FilterDropdown
                                    label="Date Range"
                                    options={dateRangeOptions}
                                    selectedValue={selectedDateRange}
                                    onChange={handleDateRangeChange}
                                />
                            )
                        )}

                        {/* GST Type filter */}
                        {showFilter && (
                            <FilterDropdown
                                label={filterLabel}
                                options={filterOptions}
                                selectedValue={selectedFilter}
                                onChange={handleFilterChange}
                                multiple={filterMultiple}
                            />
                        )}
                    </Stack>

                    {/* Right side: Action buttons */}
                    <Stack direction="row" spacing={1} alignItems="center">
                        {customActions}
                        {showCreateButton && (
                            <CommonCreateButton
                                label="Add New"
                                {...createButtonProps}
                            />
                        )}
                    </Stack>
                </Stack>
            </Box>

            {/* Error message */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Table container */}
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: tableHeight }}>
                    <Table stickyHeader size="small">
                        {/* Table header */}
                        <TableHead>
                            <TableRow>
                                {/* Checkbox column for selection */}
                                {selectable && (
                                    <TableCell padding="checkbox" sx={{ bgcolor: 'background.paper' }}>
                                        <Checkbox
                                            indeterminate={selectedRows.length > 0 && selectedRows.length < (data?.length ?? 0)}
                                            checked={(data?.length ?? 0) > 0 && selectedRows.length === (data?.length ?? 0)}
                                            onChange={handleSelectAllClick}
                                        />
                                    </TableCell>
                                )}

                                {/* Dynamic columns */}
                                {columns.map((column) => (
                                    <TableCell
                                        key={column.id}
                                        align={column.align || 'left'}
                                        sx={{
                                            minWidth: column.minWidth,
                                            bgcolor: 'background.paper',
                                            fontWeight: 600
                                        }}
                                    >
                                        {column.label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        {/* Table body */}
                        <TableBody>
                            {loading ? (
                                // Loading state
                                <TableRow>
                                    <TableCell colSpan={columns.length + (selectable ? 1 : 0)} align="center">
                                        <Box py={4}>
                                            <CircularProgress />
                                            <Typography variant="body2" sx={{ mt: 2 }}>
                                                Loading data...
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : data?.length === 0 ? (
                                // Empty state
                                <TableRow>
                                    <TableCell colSpan={columns.length + (selectable ? 1 : 0)} align="center">
                                        <Box py={4}>
                                            <Typography variant="body2" color="text.secondary">
                                                {emptyMessage}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                // Data rows
                                data?.map((row, index) => {
                                    const rowId = row[idField];
                                    const isRowSelected = selectable && isSelected(rowId);

                                    return (
                                        <TableRow
                                            hover
                                            key={rowId || index}
                                            selected={isRowSelected}
                                            onClick={(event) => clickableRows && handleRowClick(row, event)}
                                            sx={{
                                                cursor: clickableRows ? 'pointer' : 'default',
                                                '&:hover': clickableRows ? {
                                                    bgcolor: 'action.hover'
                                                } : {}
                                            }}
                                        >
                                            {/* Checkbox for row selection */}
                                            {selectable && (
                                                <TableCell
                                                    padding="checkbox"
                                                    onClick={(event) => event.stopPropagation()}
                                                >
                                                    <Checkbox
                                                        checked={isRowSelected}
                                                        onClick={(event) => handleRowSelect(rowId, event)}
                                                    />
                                                </TableCell>
                                            )}

                                            {/* Dynamic cells */}
                                            {columns.map((column) => {
                                                const value = row[column.id];

                                                return (
                                                    <TableCell
                                                        key={column.id}
                                                        align={column.align || 'left'}
                                                    >
                                                        {column.renderCell
                                                            ? column.renderCell(row)
                                                            : column.format
                                                                ? column.format(value)
                                                                : value
                                                                || "-"}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
};

export default CommonAccountTable;